<template>
  <div class="row" style="margin-top: 14px">
    <div class="col-xl-12">
      <div class="card">
        <div class="card-body p-0">
          <div class="d-flex p-3">
            <div class="flex-shrink-0">
              <div class="avatar-md me-3">
                <span class="avatar-title bg-soft-info rounded-circle fs-1">
                  <i class="bx bx-lock-open-alt text-info"></i>
                </span>
              </div>
            </div>
            <div v-if="!accessRequested">
              <p class="fs-16 lh-base">
                <b>Потрібен доступ?</b>
                <!-- <i class="mdi mdi-arrow-right"></i> -->
              </p>
              <span class="text-muted"
                >Натисніть, щоб надіслати запит адміністратору. Вкажіть тип
                доступу, розділи та час дії.</span
              >
              <div class="mt-3">
                <a class="btn btn-secondary" @click="openAccessModal"
                  >Запросити доступ</a
                >
              </div>
            </div>
            <div v-else-if="accessPending">
              <p class="fs-16 lh-base">
                <b>Запит на доступ надіслано</b>
              </p>
              <span class="text-muted"
                >Очікуємо підтвердження від адміністратора...</span
              >
              <div class="mt-3">
                <div class="spinner-border text-info" role="status">
                  <span class="visually-hidden">Завантаження...</span>
                </div>
              </div>
            </div>
            <div v-else-if="accessGranted">
              <p class="fs-16 lh-base">
                <b>Доступ надано!</b>
              </p>
              <span class="text-muted">Тепер ви можете відкрити компанію</span>
              <div class="mt-3">
                <a class="btn btn-success" @click="openCompany"
                  >Відкрити компанію</a
                >
              </div>
            </div>
          </div>
        </div>
        <!-- end card-body-->
      </div>
    </div>
  </div>

  <!-- Модальне вікно для запиту доступу -->
  <teleport to="body" v-if="showAccessModal">
    <div class="access-modal-overlay" @click.self="closeAccessModal">
      <div class="access-modal-container">
        <!-- Заголовок -->
        <div class="access-modal-header">
          <div class="access-modal-header-content">
            <div class="access-modal-icon-wrapper">
              <i class="bx bx-lock-open-alt"></i>
            </div>
            <div class="access-modal-header-text">
              <h3 class="access-modal-title">Запит доступу до компанії</h3>
              <p class="access-modal-code">ID запиту: #REQ-{{ requestId }}</p>
            </div>
          </div>
          <button class="access-modal-close" @click="closeAccessModal">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="access-modal-info">
          <p>Оберіть параметри доступу нижче.</p>
        </div>

        <!-- Тіло -->
        <div class="access-modal-body">
          <!-- Тип доступу -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-target-lock text-danger"></i>
              <h4 class="access-section-title">Тип доступу</h4>
            </div>
            <div class="access-type-grid">
              <div
                v-for="type in accessTypes"
                :key="type.value"
                :class="[
                  'access-type-card',
                  { active: accessRequest.type === type.value },
                ]"
                @click="accessRequest.type = type.value"
              >
                <i :class="type.icon"></i>
                <div class="access-type-label">{{ type.label }}</div>
                <div class="access-type-desc">{{ type.description }}</div>
              </div>
            </div>
          </div>

          <!-- Розділи -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-category text-primary"></i>
              <h4 class="access-section-title">Розділи</h4>
              <a
                v-if="accessRequest.sections.length < sections.length"
                class="access-select-all"
                @click="selectAllSections"
              >
                Обрати всі
              </a>
              <a v-else class="access-select-all" @click="clearAllSections">
                Очистити
              </a>
            </div>
            <div class="access-sections-grid">
              <div
                v-for="section in sections"
                :key="section.value"
                :class="[
                  'access-section-card',
                  { active: accessRequest.sections.includes(section.value) },
                ]"
                @click="toggleSection(section.value)"
              >
                <i :class="section.icon"></i>
                <span>{{ section.label }}</span>
              </div>
            </div>
          </div>

          <!-- Тривалість доступу -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-time text-info"></i>
              <h4 class="access-section-title">Тривалість доступу</h4>
            </div>
            <div class="access-duration-grid">
              <div
                v-for="duration in durations"
                :key="duration.value"
                :class="[
                  'access-duration-card',
                  { active: accessRequest.lifetimeHours === duration.value },
                ]"
                @click="accessRequest.lifetimeHours = duration.value"
              >
                <div class="access-duration-number">{{ duration.value }}</div>
                <div class="access-duration-label">годин</div>
              </div>
            </div>
          </div>

          <!-- Коментар -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-message-square-detail"></i>
              <h4 class="access-section-title">Коментар</h4>
              <span class="access-optional">необов'язково</span>
            </div>
            <textarea
              v-model="accessRequest.comment"
              class="access-comment-input"
              rows="3"
              placeholder="Додайте коментар або опис проблеми, яку потрібно вирішити..."
            ></textarea>
          </div>

          <!-- Попередження -->
          <div class="access-warning">
            <i class="bx bx-info-circle"></i>
            <div>
              <strong>Важлива інформація:</strong> Доступ буде автоматично
              відкликано після закінчення вказаного терміну. Всі дії спеціаліста
              будуть записані в журнал подій та доступні для перегляду.
            </div>
          </div>
        </div>

        <!-- Футер -->
        <div class="access-modal-footer">
          <button
            type="button"
            class="access-btn access-btn-cancel"
            @click="closeAccessModal"
          >
            Скасувати
          </button>
          <button
            type="button"
            class="access-btn access-btn-submit"
            @click="submitAccessRequest"
            :disabled="accessRequest.sections.length === 0"
          >
            Надіслати запит
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!-- Модальне вікно перегляду запису -->
  <teleport to="body" v-if="showViewModal">
    <div class="access-modal-overlay" @click.self="closeViewModal">
      <div class="access-modal-container">
        <!-- Заголовок -->
        <div class="access-modal-header access-view-header">
          <div class="access-modal-header-content">
            <div class="access-modal-icon-wrapper access-view-icon">
              <i class="bx bx-show"></i>
            </div>
            <div class="access-modal-header-text">
              <h3 class="access-modal-title">Перегляд запису доступу</h3>
              <p class="access-modal-code">
                ID запиту: #REQ-{{ viewRecord.requestId }}
              </p>
            </div>
          </div>
          <button class="access-modal-close" @click="closeViewModal">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <!-- Інформація про запис -->
        <div class="access-modal-info">
          <div class="access-view-info-grid">
            <div class="access-view-info-item">
              <span class="access-view-info-label">Спеціаліст:</span>
              <span class="access-view-info-value">{{ viewRecord.user }}</span>
            </div>
            <div class="access-view-info-item">
              <span class="access-view-info-label">Тип доступу:</span>
              <span
                :class="[
                  'access-view-badge',
                  getAccessTypeClass(viewRecord.accessType),
                ]"
              >
                {{
                  mutateAccessType({ accessType: viewRecord.accessType }).name
                }}
              </span>
            </div>
            <div class="access-view-info-item">
              <span class="access-view-info-label">Статус:</span>
              <span
                :class="[
                  'access-view-badge',
                  getStatusClass(viewRecord.status),
                ]"
              >
                {{ mutateConnectStatus({ status: viewRecord.status }).name }}
              </span>
            </div>
            <div class="access-view-info-item">
              <span class="access-view-info-label">Надано:</span>
              <span class="access-view-info-value">{{
                viewRecord.grantedAt
              }}</span>
            </div>
            <div class="access-view-info-item">
              <span class="access-view-info-label">Дійсний до:</span>
              <span class="access-view-info-value">{{ viewRecord.until }}</span>
            </div>
            <div class="access-view-info-item">
              <span class="access-view-info-label">Тривалість:</span>
              <span class="access-view-info-value"
                >{{ viewRecord.lifetimeHours }} годин</span
              >
            </div>
          </div>
        </div>

        <!-- Тіло -->
        <div class="access-modal-body">
          <!-- Розділи -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-category text-primary"></i>
              <h4 class="access-section-title">Дозволені розділи</h4>
            </div>
            <div class="access-view-chips">
              <span
                v-for="section in activeChips"
                :key="section.key"
                class="access-view-chip"
              >
                <i :class="getSectionIcon(section.key)"></i>
                {{ getSectionLabel(section.key) }}
              </span>
            </div>
          </div>

          <!-- Коментар -->
          <div class="access-section" v-if="viewRecord.comment">
            <div class="access-section-header">
              <i class="bx bx-message-square-detail"></i>
              <h4 class="access-section-title">Коментар</h4>
            </div>
            <div class="access-view-comment">
              {{ viewRecord.comment }}
            </div>
          </div>

          <!-- Журнал дій -->
          <div class="access-section">
            <div class="access-section-header">
              <i class="bx bx-history text-warning"></i>
              <h4 class="access-section-title">Журнал дій користувача</h4>
            </div>
            <div class="access-log-container">
              <div
                v-for="(log, index) in viewRecord.activityLog"
                :key="index"
                class="access-log-item"
              >
                <div class="access-log-time">{{ log.time }}</div>
                <div class="access-log-indicator"></div>
                <div class="access-log-content">
                  <div class="access-log-action">
                    <i :class="log.icon"></i>
                    {{ log.action }}
                  </div>
                  <div class="access-log-details" v-if="log.details">
                    {{ log.details }}
                  </div>
                </div>
              </div>
              <div
                v-if="
                  !viewRecord.activityLog || viewRecord.activityLog.length === 0
                "
                class="access-log-empty"
              >
                <i class="bx bx-info-circle"></i>
                <p>Дій ще не було виконано</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Футер -->
        <div class="access-modal-footer">
          <button
            type="button"
            class="access-btn access-btn-cancel"
            @click="closeViewModal"
          >
            Закрити
          </button>
          <button
            type="button"
            class="access-btn access-btn-open-company"
            v-if="viewRecord.status === 'active'"
            @click="openCompanyFromView"
          >
            <i class="bx bx-log-in"></i>
            Відкрити компанію
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <tablecustom
    :columns="columns"
    :rows="rows"
    :tabs="othertabs"
    :addtabs="false"
    @changeTab="changeTab"
    @changeFilter="changeFilter"
    @clearFilter="clearFilter"
    @open="openViewRecord"
  />
</template>

<script>
import tablecustom from "@/components/globaltable/index";
import { storeS } from "@/store";
import {
  mutateAccessType,
  mutateConnectStatus,
} from "@/usabilityScripts/globalMutate.js";

export default {
  props: [],
  components: {
    tablecustom,
  },
  data() {
    return {
      // Стан запиту доступу
      showAccessModal: false,
      accessRequested: false,
      accessPending: false,
      accessGranted: false,

      // Стан модального вікна перегляду
      showViewModal: false,
      viewRecord: {
        requestId: "",
        user: "",
        accessType: "",
        grantedAt: "",
        until: "",
        status: "",
        chips: {},
        lifetimeHours: "",
        comment: "",
        activityLog: [],
      },

      // Дані форми запиту доступу
      accessRequest: {
        type: "read",
        sections: [],
        lifetimeHours: "6",
        comment: "",
      },

      // Інформація про запит
      currentUser: "Марія Петренко",
      requestId: "5829",

      // Типи доступу
      accessTypes: [
        {
          value: "read",
          label: "Читання",
          description: "Тільки перегляд",
          icon: "bx bx-show",
        },
        {
          value: "full_access",
          label: "Повний",
          description: "Всі можливості",
          icon: "bx bx-lock-open",
        },
      ],

      // Доступні розділи
      sections: [
        { value: "kassa", label: "Каса", icon: "bx bx-money" },
        { value: "finance", label: "Фінанси", icon: "bx bx-wallet" },
        { value: "marketing", label: "Маркетинг", icon: "bx bx-bullhorn" },
        { value: "products", label: "Товари", icon: "bx bx-package" },
        { value: "program", label: "Програма", icon: "bx bx-laptop" },
        { value: "settings", label: "Налаштування", icon: "bx bx-cog" },
        { value: "statistics", label: "Статистика", icon: "bx bx-bar-chart" },
      ],

      // Тривалість доступу
      durations: [
        { value: "6", label: "6 годин" },
        { value: "8", label: "8 годин" },
        { value: "12", label: "12 годин" },
      ],

      othertabs: [
        {
          //   title: this.$t("Replenishment"),
          title: "Активні доступи",
          tab: "paymentAction1",
          color: "#2cb29c",
          obj: "paymentAction",
          value: "pay",
          checked: true,
        },
        {
          //   title: this.$t("WriteOff"),
          title: "Пасивні доступи",
          tab: "paymentAction0",
          color: "#ec6753",
          obj: "paymentAction",
          value: "writeoff",
          checked: true,
        },
      ],
      rows: [
        {
          user: "Юрій Красота",
          accessType: "read",
          grantedAt: "28.10.2025 13:50",
          until: "29.10.2025 13:50",
          status: "active",
          chips: {
            kassa: true,
            finance: true,
            marketing: false,
            products: false,
            program: false,
            settings: false,
            statistics: true,
          },
          lifetimeHours: "6",
          comment: "Потрібно перевірити касові операції",
          activityLog: [
            {
              time: "28.10.2025 13:52",
              action: "Відкрив компанію",
              icon: "bx bx-log-in text-success",
              details: "Вхід у систему успішний",
            },
            {
              time: "28.10.2025 13:55",
              action: "Переглянув касу",
              icon: "bx bx-show text-info",
              details: "Розділ: Каса",
            },
            {
              time: "28.10.2025 14:02",
              action: "Змінив налаштування",
              icon: "bx bx-edit text-warning",
              details: "Оновлено параметри фіскалізації",
            },
            {
              time: "28.10.2025 14:10",
              action: "Переглянув статистику",
              icon: "bx bx-bar-chart text-info",
              details: "Розділ: Статистика продажів",
            },
          ],
        },
        {
          user: "Марія Петренко",
          accessType: "full_access",
          grantedAt: "25.10.2025 10:50",
          until: "26.10.2025 10:50",
          status: "noactive",
          chips: {
            kassa: true,
            finance: true,
            marketing: true,
            products: true,
            program: true,
            settings: true,
            statistics: true,
          },
          lifetimeHours: "12",
          comment: "Повне налаштування системи для нового користувача",
          activityLog: [
            {
              time: "25.10.2025 10:52",
              action: "Відкрив компанію",
              icon: "bx bx-log-in text-success",
              details: "Вхід у систему успішний",
            },
            {
              time: "25.10.2025 11:00",
              action: "Додав нового працівника",
              icon: "bx bx-user-plus text-success",
              details: "ПІБ: Іван Іванов",
            },
            {
              time: "25.10.2025 11:15",
              action: "Налаштував фінанси",
              icon: "bx bx-wallet text-info",
              details: "Оновлено банківські реквізити",
            },
            {
              time: "25.10.2025 11:30",
              action: "Закрив сесію",
              icon: "bx bx-log-out text-danger",
              details: "Вихід із системи",
            },
          ],
        },
      ],
      columns: [
        {
          name: "Кому",
          text: "user",
          align: "right",
          status: true,
          copy: true,
        },
        {
          name: "Тип доступу",
          text: "accessType",
          align: "right",
          status: true,
          copy: true,
          mutate: (item) => this.mutateAccessType(item).name,
          mutateClass: (item) => this.mutateAccessType(item).color_bagde,
        },
        {
          name: "Надано",
          text: "grantedAt",
          align: "right",
          status: true,
          copy: true,
        },
        {
          name: "Дійсний до",
          text: "until",
          align: "right",
          status: true,
          copy: true,
        },
        {
          name: "Статус",
          text: "status",
          align: "right",
          status: true,
          copy: true,
          mutate: (item) => this.mutateConnectStatus(item).name,
          mutateClass: (item) => this.mutateConnectStatus(item).color,
        },
      ],
    };
  },
  created() {
    this.getdata();
  },
  computed: {
    activeChips() {
      if (!this.viewRecord.chips) return [];
      return Object.entries(this.viewRecord.chips)
        .filter(([key, value]) => value === true)
        .map(([key]) => ({ key }));
    },
  },
  methods: {
    getdata() {
      console.log("getdata");
    },
    mutateAccessType(e) {
      return mutateAccessType(e);
    },
    mutateConnectStatus(e) {
      return mutateConnectStatus(e);
    },

    // Методи для роботи з модальним вікном запиту доступу
    openAccessModal() {
      this.showAccessModal = true;
    },

    closeAccessModal() {
      this.showAccessModal = false;
      // Скидаємо форму при закритті
      this.resetAccessForm();
    },

    resetAccessForm() {
      this.accessRequest = {
        type: "read",
        sections: [],
        lifetimeHours: "6",
        comment: "",
      };
    },

    toggleSection(sectionValue) {
      const index = this.accessRequest.sections.indexOf(sectionValue);
      if (index > -1) {
        this.accessRequest.sections.splice(index, 1);
      } else {
        this.accessRequest.sections.push(sectionValue);
      }
    },

    selectAllSections() {
      this.accessRequest.sections = this.sections.map((s) => s.value);
    },

    clearAllSections() {
      this.accessRequest.sections = [];
    },

    formatDateTime(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const months = [
        "січня",
        "лютого",
        "березня",
        "квітня",
        "травня",
        "червня",
        "липня",
        "серпня",
        "вересня",
        "жовтня",
        "листопада",
        "грудня",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year} р. о ${hours}:${minutes}`;
    },

    submitAccessRequest() {
      // Закриваємо модальне вікно
      this.showAccessModal = false;

      // Встановлюємо стан очікування
      this.accessRequested = true;
      this.accessPending = true;

      // Тут має бути API запит на створення запиту доступу
      // Наприклад: this.$api.requestAccess(this.accessRequest)

      // Симуляція відповіді від сервера (для демонстрації)
      // В реальному проекті це буде API запит
      setTimeout(() => {
        this.accessPending = false;
        this.accessGranted = true;
      }, 3000); // Симуляція затримки 3 секунди

      console.log("Access request submitted:", this.accessRequest);
    },

    openCompany() {
      // Логіка відкриття компанії
      // Наприклад, редірект або емітування події
      console.log("Opening company...");
      // this.$router.push('/company/...')
    },

    // Методи для роботи з модальним вікном перегляду
    openViewRecord(record) {
      this.viewRecord = {
        requestId: this.generateRequestId(),
        user: record.user,
        accessType: record.accessType,
        grantedAt: record.grantedAt,
        until: record.until,
        status: record.status,
        chips: record.chips || {},
        lifetimeHours: record.lifetimeHours,
        comment: record.comment || "",
        activityLog: record.activityLog || [],
      };
      this.showViewModal = true;
    },

    closeViewModal() {
      this.showViewModal = false;
    },

    generateRequestId() {
      return Math.floor(1000 + Math.random() * 9000).toString();
    },

    openCompanyFromView() {
      console.log("Opening company from view record...");
      // this.$router.push('/company/...')
      this.closeViewModal();
    },

    getSectionIcon(sectionKey) {
      const iconMap = {
        kassa: "bx bx-money",
        finance: "bx bx-wallet",
        marketing: "bx bx-bullhorn",
        products: "bx bx-package",
        program: "bx bx-laptop",
        settings: "bx bx-cog",
        statistics: "bx bx-bar-chart",
      };
      return iconMap[sectionKey] || "bx bx-circle";
    },

    getSectionLabel(sectionKey) {
      const labelMap = {
        kassa: "Каса",
        finance: "Фінанси",
        marketing: "Маркетинг",
        products: "Товари",
        program: "Програма",
        settings: "Налаштування",
        statistics: "Статистика",
      };
      return labelMap[sectionKey] || sectionKey;
    },

    getAccessTypeClass(accessType) {
      const classMap = {
        read: "badge-info",
        edit: "badge-warning",
        full_access: "badge-danger",
      };
      return classMap[accessType] || "badge-secondary";
    },

    getStatusClass(status) {
      const classMap = {
        active: "badge-success",
        noactive: "badge-secondary",
      };
      return classMap[status] || "badge-secondary";
    },
  },
};
</script>

<style scoped>
/* Модальне вікно overlay */
.access-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Контейнер модального вікна */
.access-modal-container {
  background: white;
  border-radius: 16px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Заголовок */
.access-modal-header {
  background: #fff;
  padding: 24px 24px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #eff2f7;
}

.access-modal-header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.access-modal-icon-wrapper {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.access-modal-icon-wrapper i {
  font-size: 20px;
  color: #74788d;
}

.access-modal-header-text {
  flex: 1;
}

.access-modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #495057;
}

.access-modal-code {
  font-size: 12px;
  margin: 0;
  color: #74788d;
  font-weight: 400;
}

.access-modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #74788d;
  flex-shrink: 0;
}

.access-modal-close:hover {
  background: #f8f9fa;
}

.access-modal-close i {
  font-size: 20px;
}

/* Інформаційний блок */
.access-modal-info {
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #eff2f7;
}

.access-modal-info p {
  margin: 0;
  font-size: 13px;
  color: #74788d;
  line-height: 1.5;
}

/* Тіло модального вікна */
.access-modal-body {
  background: white;
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Секція */
.access-section {
  margin-bottom: 20px;
}

.access-section:last-of-type {
  margin-bottom: 0;
}

.access-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.access-section-header i {
  font-size: 16px;
  color: #74788d;
}

.access-section-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #495057;
  flex: 1;
}

.access-optional {
  color: #a6b0cf;
  font-size: 12px;
  font-style: italic;
}

.access-select-all {
  color: #556ee6;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
}

.access-select-all:hover {
  text-decoration: underline;
}

/* Сітка типів доступу */
.access-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.access-type-card {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 8px;
  padding: 14px 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.access-type-card:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.access-type-card.active {
  background: #556ee6;
  border-color: #556ee6;
  color: white;
}

.access-type-card i {
  font-size: 24px;
  margin-bottom: 6px;
  display: block;
}

.access-type-label {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}

.access-type-desc {
  font-size: 11px;
  opacity: 0.8;
}

/* Сітка розділів */
.access-sections-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.access-section-card {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.access-section-card:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.access-section-card.active {
  background: #556ee6;
  border-color: #556ee6;
  color: white;
}

.access-section-card i {
  font-size: 18px;
}

/* Сітка тривалості */
.access-duration-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.access-duration-card {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.access-duration-card:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.access-duration-card.active {
  background: #556ee6;
  border-color: #556ee6;
  color: white;
}

.access-duration-number {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 2px;
}

.access-duration-label {
  font-size: 12px;
  opacity: 0.8;
}

/* Поле коментаря */
.access-comment-input {
  width: 100%;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  background: #fff;
}

.access-comment-input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
}

.access-comment-input::placeholder {
  color: #a6b0cf;
}

/* Попередження */
.access-warning {
  background: #fef5e7;
  border: 1px solid #f8e4c0;
  border-radius: 6px;
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  margin-top: 16px;
  font-size: 12px;
  color: #74788d;
}

.access-warning i {
  font-size: 18px;
  color: #f1b44c;
  flex-shrink: 0;
}

/* Кроки для вирішення */
.access-modal-steps {
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #eff2f7;
}

.access-steps-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #495057;
}

.access-steps-content {
  font-size: 12px;
  color: #74788d;
  line-height: 1.5;
}

.access-steps-description {
  margin: 0;
}

/* Футер */
.access-modal-footer {
  background: white;
  padding: 16px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #eff2f7;
}

.access-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.access-btn-cancel {
  background: #fff;
  color: #74788d;
  border: 1px solid #eff2f7;
}

.access-btn-cancel:hover {
  background: #f8f9fa;
  border-color: #d6dae0;
}

.access-btn-submit {
  background: #556ee6;
  color: white;
}

.access-btn-submit:hover:not(:disabled) {
  background: #4a5fc1;
}

.access-btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #a6b0cf;
}

/* Стилі для модального вікна перегляду */
.access-view-header {
  background: #fff;
  border-bottom-color: #eff2f7;
}

.access-view-header .access-modal-icon-wrapper {
  background: #e8f4f8;
}

.access-view-header .access-modal-icon-wrapper i {
  color: #50a5f1;
}

.access-view-header .access-modal-title {
  color: #495057;
}

.access-view-header .access-modal-code {
  color: #74788d;
}

.access-view-header .access-modal-close {
  color: #74788d;
}

.access-view-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.access-view-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.access-view-info-label {
  font-size: 12px;
  color: #74788d;
  font-weight: 500;
}

.access-view-info-value {
  font-size: 13px;
  color: #495057;
  font-weight: 600;
}

.access-view-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.badge-info {
  background: #e8f4f8;
  color: #50a5f1;
}

.badge-warning {
  background: #fef5e7;
  color: #f1b44c;
}

.badge-danger {
  background: #fce8e8;
  color: #f46a6a;
}

.badge-success {
  background: #e8f5e9;
  color: #34c38f;
}

.badge-secondary {
  background: #f8f9fa;
  color: #74788d;
}

.access-view-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.access-view-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8f9fe;
  border: 1px solid #e8eaf6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #556ee6;
}

.access-view-chip i {
  font-size: 14px;
}

.access-view-comment {
  background: #f8f9fa;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #74788d;
  line-height: 1.5;
}

/* Журнал дій */
.access-log-container {
  background: #fff;
  border: 1px solid #eff2f7;
  border-radius: 6px;
  padding: 14px;
  max-height: 400px;
  overflow-y: auto;
}

.access-log-item {
  display: grid;
  grid-template-columns: 120px 16px 1fr;
  gap: 12px;
  padding: 10px 0;
  position: relative;
}

.access-log-item:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 132px;
  top: 28px;
  bottom: -10px;
  width: 1px;
  background: #eff2f7;
}

.access-log-time {
  font-size: 11px;
  color: #a6b0cf;
  font-weight: 500;
  padding-top: 2px;
}

.access-log-indicator {
  width: 8px;
  height: 8px;
  background: #556ee6;
  border-radius: 50%;
  margin-top: 4px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #eff2f7;
  flex-shrink: 0;
}

.access-log-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.access-log-action {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 6px;
}

.access-log-action i {
  font-size: 16px;
}

.access-log-details {
  font-size: 12px;
  color: #a6b0cf;
  padding-left: 22px;
}

.access-log-empty {
  text-align: center;
  padding: 30px 20px;
  color: #a6b0cf;
}

.access-log-empty i {
  font-size: 40px;
  color: #d6dae0;
  margin-bottom: 10px;
  display: block;
}

.access-log-empty p {
  margin: 0;
  font-size: 13px;
}

.access-btn-open-company {
  background: #34c38f;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
}

.access-btn-open-company:hover {
  background: #2ca87f;
}

.access-btn-open-company i {
  font-size: 16px;
}

/* Адаптивність */
@media (max-width: 768px) {
  .access-modal-container {
    border-radius: 16px;
  }

  .access-type-grid {
    grid-template-columns: 1fr;
  }

  .access-sections-grid {
    grid-template-columns: 1fr;
  }

  .access-duration-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .access-modal-body {
    padding: 20px;
  }

  .access-modal-footer {
    flex-direction: column;
  }

  .access-btn {
    width: 100%;
    justify-content: center;
  }

  .access-view-info-grid {
    grid-template-columns: 1fr;
  }

  .access-log-item {
    grid-template-columns: 100px 20px 1fr;
  }

  .access-log-item:not(:last-child)::after {
    left: 112px;
  }
}
</style>
